<script lang="ts" setup>
import _ from 'lodash'
import { computed, provide, type Ref, ref } from 'vue'

import SancareMultiselectItem from './SancareMultiselectItem.vue'
import SancareOcticon from './SancareOcticon.vue'
import type { MultiSelectItem } from './types'

/**
 * Multiselect with groups feature
 */

const emit = defineEmits(['update-selection'])
const props = withDefaults(
  defineProps<{
    items: MultiSelectItem[]
    selectedItems: (number | string)[]
    title?: string | null
    displayListLimit?: number
    isReady?: boolean
    allLabel?: string
    noneLabel?: string
  }>(),
  {
    title: null,
    displayListLimit: 3,
    isReady: true,
    allLabel: 'Tous',
    noneLabel: 'Aucun',
  },
)

const openPopover = ref(false)

const options = computed(() => getOptions(props.items))

const localGroups = ref([]) as Ref<MultiSelectItem[]>
const localOptions = computed(() => getOptions(localGroups.value))
const optionValues = computed(() => options.value.map((opt) => opt.value))
function getOptions(items: MultiSelectItem[]) {
  return items.reduce((options, item) => {
    options.push(...getGroupOptions(item))
    return options
  }, [])
}

function getGroupOptions(node: MultiSelectItem) {
  return node.children
    ? node.children.reduce((options, item) => {
      options.push(...getGroupOptions(item))
      return options
    }, [])
    : [node]
}

function updateItem(path, selected) {
  // Root group
  const tree = localGroups.value.find((group) => group.value === path[0])

  const itemPath = path.slice(1)
  const itemToChange = itemPath.reduce((current, step) => {
    return current.children.find((child) => child.value === step)
  }, tree)

  itemToChange.selected = selected
}

// Share this method to MultiselectItem components so that they can update option tree selected flags themselves
// Otherwise they would mutate their item prop, which is not advised by Vue. See: https://stackoverflow.com/questions/60672605/how-to-avoid-mutating-a-prop-directly-in-a-recursive-component/60675023#60675023
provide('items', {
  updateItem,
})

const isAll = computed(() => props.selectedItems.length === options.value.length)

const isNone = computed(() => props.selectedItems.length === 0)
const isAllLocal = computed(() => localOptions.value.length === localSelectedItems.value.length)
const sortedItems = computed(() => _.sortBy(options.value, ['label']))

const sortedSelectedItems = computed(() => {
  if (isAll.value) {
    return sortedItems.value
  }

  return sortedItems.value.filter((item) => props.selectedItems.includes(item.value))
})

const localSelectedItems = computed(() => localOptions.value.filter((opt) => opt.selected).map((opt) => opt.value))

function open() {
  openPopover.value = true
  initItems()
}

function initItems() {
  const groups = _.cloneDeep(props.items)
  localGroups.value = buildGroups(groups, props.selectedItems)
}

function buildGroups(groups: MultiSelectItem[], selectedItems: (number | string)[]) {
  return groups
    .map((item) => selectGroupOptions(item, selectedItems))
    .filter((item) => !item.children || item.children.length > 0)
}

function selectGroupOptions(node: MultiSelectItem, selectedItems: (number | string)[]) {
  if (!node.children) {
    node.selected = selectedItems.includes(node.value)
  } else {
    node.children = node.children
      .filter((item) => !item.children || item.children.length > 0)
      .map((child) => selectGroupOptions(child, selectedItems))
    node.selected = node.children.every((child) => child.selected)
  }
  return node
}

function close(cancel: boolean) {
  if (!cancel) {
    emit('update-selection', localSelectedItems.value)
  }
  openPopover.value = false
}

function selectAll() {
  if (isAllLocal.value) {
    localGroups.value = buildGroups(localGroups.value, [])
    return
  }

  localGroups.value = buildGroups(localGroups.value, optionValues.value)
}
</script>

<template>
  <div>
    <span v-if="title !== null">{{ title }} : </span>
    <v-dropdown
      v-model:shown="openPopover"
      class="d-inline-block"
      @auto-hide="close(true)"
    >
      <span
        class="sancare-picker py-1"
        @click.prevent="open"
      >
        <span
          v-if="!isReady"
          class="px-3"
        >...</span>
        <span v-else-if="isAll || isNone">{{ isAll ? allLabel : noneLabel }}</span>
        <span v-else-if="selectedItems && selectedItems.length > displayListLimit">{{ selectedItems.length }} éléments</span>
        <span
          v-for="item in sortedSelectedItems"
          v-else
          :key="item.value"
          class="badge text-bg-info mx-1"
        >{{ item.label }}</span>
      </span>
      <template #popper>
        <div class="multiselect-popover container py-3">
          <div class="row border-bottom">
            <h3 class="col-auto me-auto ">
              {{ title }}
            </h3>
            <div class="col-auto">
              <span
                class="cp"
                @click.prevent="close(true)"
              >
                <sancare-octicon
                  name="x"
                  :width="20"
                  :height="20"
                />
              </span>
            </div>
          </div>
          <div class="multiselect-popover__body row pt-2 ">
            <label
              class="col-12 mb-1"
              for="sms_all"
              @click.prevent="selectAll"
            >
              <input
                id="sms_all"
                :checked="isAllLocal"
                type="checkbox"
                name="sms_all"
              >
              {{ allLabel }}
            </label>
            <SancareMultiselectItem
              v-for="item in localGroups"
              :key="item.value"
              :item="item"
            />
          </div>
          <div class="row border-top pt-2">
            <div class="col d-flex justify-content-end">
              <div
                class="btn btn-primary ms-auto"
                @click.prevent="close(false)"
              >
                Valider
              </div>
            </div>
          </div>
        </div>
      </template>
    </v-dropdown>
  </div>
</template>
